import { SharesCellData, kind } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { DECIMAL_SCALE } from '../constants';

const getDefaultSharesCellData = ({
  value = null,
  error = null,
}: Partial<SharesCellData> = {}): SharesCellData => ({
  kind,
  value,
  error,
});

const isCellData = (data: unknown): data is SharesCellData => {
  if (!isCustomCellData(data)) return false;
  return data.kind === kind;
};

type Args = Pick<Partial<SharesCellData>, 'error'> & { value?: unknown };

/**
 * ## getCellTemplate
 *
 * @param value The value of the SharesCell
 * @returns A custom SharesCell object and its data
 */
export const getCellTemplate = ({ value = null, error = null }: Args = {}) => {
  if (typeof value === 'number' || value === null) {
    const copyData = String(value ?? '');
    const cellData = getDefaultSharesCellData({ value, error });
    return getCustomCellTemplate({ cellData, copyData });
  }
  return getCustomCellTemplate({ cellData: getDefaultSharesCellData() });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data?: unknown) => {
  if (isCellData(data) && typeof data.value === 'number') {
    return data;
  }

  if (isCellData(data) && data.error !== null) {
    return data;
  }

  return getDefaultSharesCellData();
};

export const getValueAsString = (value: number | null) => {
  if (value === null) return '';
  if (Number.isNaN(value)) return '';
  //  Deal with exponential notation
  if (value.toString().includes('e-')) {
    return String(value.toFixed(DECIMAL_SCALE));
  }
  return String(value);
};

export const validatePastedValue = (
  pastedValue: string,
  cell: SharesCellData
): SharesCellData => {
  // If you paste a value with 1 comma AND 1 dot (eg. 1,000.89 or 1.000,90), then throw an error
  if (pastedValue.includes(',') && pastedValue.includes('.')) {
    return { ...cell, error: 'No commas, only dots' };
  }

  // If you paste a value with a comma (eg. 10,98), then convert to dot (10.98)
  // If you paste a value with multiple comma (eg. 10,005,000.50), this converts to (10.005)
  const value = Number.parseFloat(pastedValue.replace(',', '.'));

  if (Number.isNaN(value)) {
    return { ...cell, value: null, error: 'Enter numbers only' };
  }

  return {
    ...cell,
    value: Math.abs(value),
    error: null,
  };
};
